import { useTheme } from "@emotion/react";
import {
  CheckBox,
  CheckBoxOutlineBlank,
  ExpandMore,
  GroupsOutlined,
  TuneOutlined,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { useEffect, useRef, useState } from "react";
import api from "../../../../../../services/api";
import ErpChip from "../../../../../../components/erp/ErpChip";
import ErpFilter from "../../../../../../components/erp/ErpFilter";
import useErpSelecionado from "../../../../../../components/erp/useErpSelecionado";
import useStyles from "./styles";

// De onde vem o filtro aplicado na importação de clientes.
const ORIGENS = {
  CONFIGURACAO: { label: "Configurado", color: "success" },
  VARIAVEL_AMBIENTE: { label: "Variável de ambiente", color: "warning" },
  SEM_FILTRO: { label: "Sem filtro", color: "default" },
};

const labelGrupo = (grupo) =>
  grupo ? `${grupo.id} - ${grupo.descricao || ""}` : "";

// Grupo pelo id; mantém visível um id salvo que não está mais ativo no ERP.
const grupoPorId = (opcoes, id) =>
  opcoes?.find((g) => g.id === id) || {
    id,
    descricao: "(não encontrado entre os grupos ativos do ERP)",
  };

// O campo mostra só o que está salvo nesta configuração (vazio = não
// configurado); o filtro efetivo aparece em "Em uso".
const gruposConfigurados = (dados) =>
  (dados?.idsConfigurados || []).map((id) => grupoPorId(dados?.opcoes, id));

const mesmosIds = (a, b) =>
  a.length === b.length && a.every((id) => b.includes(id));

export default function ConfigIntegracaoErp() {
  const theme = useTheme();
  const styles = useStyles(theme);
  // O filtro é de cada HubSoft (HUBSOFT A e B têm o seu).
  const { erps, erpId, setErpId, carregado, variosErps } = useErpSelecionado([
    "HUBSOFT",
  ]);
  const erpAtualRef = useRef(erpId);
  erpAtualRef.current = erpId;

  const [snackbar, setSnackbar] = useState(null);
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [config, setConfig] = useState(null);
  const [selecionados, setSelecionados] = useState([]);
  const [isEdit, setIsEdit] = useState(false);

  const aplicar = (dados) => {
    setConfig(dados);
    setSelecionados(gruposConfigurados(dados));
  };

  const handleEdit = () => setIsEdit(true);

  const handleCancel = () => {
    setIsEdit(false);
    setSelecionados(gruposConfigurados(config));
  };

  const getConfig = async () => {
    const erpPedido = erpId;
    setLoading(true);
    try {
      const response = await api.post("/interno/find/configIntegracaoErp", {
        erpId: erpPedido,
      });
      if (erpPedido !== erpAtualRef.current) return; // trocou de ERP no meio
      aplicar(response?.data);
    } catch (error) {
      console.log(error);
      setSnackbar({
        children:
          error?.response?.data?.error ||
          "Erro ao buscar os grupos de cliente do ERP!",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!carregado) return;
    setIsEdit(false);
    getConfig();
  }, [carregado, erpId]);

  const salvar = async () => {
    setSalvando(true);
    try {
      const response = await api.post("/interno/update/configIntegracaoErp", {
        erpId: config?.erp?.id || erpId,
        idsGrupoCliente: selecionados.map((g) => g.id),
      });
      aplicar(response?.data);
      setIsEdit(false);
      setSnackbar({
        children: selecionados.length
          ? "Filtro de grupos de cliente atualizado com sucesso!"
          : "Configuração removida. Este HubSoft voltou para a variável de ambiente (se for o HubSoft em uso) ou, sem ela, todos os clientes.",
        severity: "success",
      });
    } catch (error) {
      console.log(error);
      setSnackbar({
        children:
          error?.response?.data?.error ||
          "Erro ao salvar o filtro de grupos de cliente!",
        severity: "error",
      });
    } finally {
      setSalvando(false);
    }
  };

  const emUso = (config?.idsGrupoCliente || []).map((id) =>
    grupoPorId(config?.opcoes, id),
  );
  const origem = ORIGENS[config?.origem];
  const alterado = !mesmosIds(
    selecionados.map((g) => g.id),
    config?.idsConfigurados || [],
  );

  return (
    <>
      <Accordion data-secao="integracao">
        <AccordionSummary
          expandIcon={<ExpandMore />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          <Box sx={styles.containerHeader}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TuneOutlined color="primary" />
              <Typography variant="h5" sx={{ ...styles.title }}>
                Configurações da integração ERP
              </Typography>
              {/* Só existe na HubSoft: o selo diz de qual HubSoft é. */}
              <ErpChip erp={config?.erp || { tipo: "HUBSOFT" }} />
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {variosErps && (
            <ErpFilter
              erps={erps}
              value={erpId}
              onChange={(id) => id && setErpId(id)}
              limpavel={false}
              label="HubSoft"
              id="filtro-erp-integracao"
              sx={{ minWidth: 260, maxWidth: 360, mb: 1.5 }}
            />
          )}
          <Paper variant="outlined" sx={styles.paperBody}>
            {loading ? (
              <Box sx={styles.loading}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Box>
                  <Box sx={styles.headerBox}>
                    <GroupsOutlined color="primary" fontSize="small" />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Filtro de clientes por grupo de cliente
                      {config?.erp && ` — ${config.erp.nome}`}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={styles.atualBox}>
                  <Typography sx={styles.atualLabel}>Em uso:</Typography>
                  <Typography sx={styles.atualValor}>
                    {emUso.length
                      ? emUso.map(labelGrupo).join(", ")
                      : "Todos os clientes"}
                  </Typography>
                  {origem && (
                    <Chip
                      size="small"
                      variant="outlined"
                      color={origem.color}
                      label={origem.label}
                    />
                  )}
                </Box>

                {!config?.erp && (
                  <Alert severity="warning" sx={styles.alert}>
                    Para salvar, cadastre o ERP HubSoft em Administração › Geral
                    › Configuração interna.
                  </Alert>
                )}

                <Autocomplete
                  data-busca="integracao-grupos"
                  multiple
                  disableCloseOnSelect
                  size="small"
                  options={config?.opcoes || []}
                  value={selecionados}
                  disabled={!isEdit || salvando || !config?.erp}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  getOptionLabel={labelGrupo}
                  onChange={(event, value) => setSelecionados(value)}
                  noOptionsText="Nenhum grupo de cliente ativo encontrado no ERP"
                  renderOption={(props, option, { selected }) => {
                    const { key, ...optionProps } = props;
                    return (
                      <li key={key} {...optionProps}>
                        <Checkbox
                          icon={<CheckBoxOutlineBlank fontSize="small" />}
                          checkedIcon={<CheckBox fontSize="small" />}
                          sx={styles.checkbox}
                          checked={selected}
                        />
                        {labelGrupo(option)}
                      </li>
                    );
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={labelGrupo(option)}
                        size="small"
                      />
                    ))
                  }
                  sx={styles.autocomplete}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      variant="filled"
                      label="Grupos de cliente"
                      placeholder={selecionados.length ? "" : "Não configurado"}
                      InputLabelProps={{ shrink: true }}
                      helperText="Somente HubSoft. Deixe vazio e salve para remover o filtro deste HubSoft (o HubSoft em uso volta para a variável de ambiente, se houver; sem ela, todos os clientes)"
                    />
                  )}
                />

                {isEdit && (
                  <Alert severity="info" sx={styles.alert}>
                    A alteração vale a partir da próxima sincronização de
                    clientes. Clientes fora dos grupos selecionados deixam de
                    aparecer na lista de clientes do ERP.
                  </Alert>
                )}
              </>
            )}
          </Paper>

          {!loading && (
            <Box sx={styles.boxActions}>
              {!isEdit ? (
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleEdit}
                  disabled={!config?.erp}
                >
                  Editar
                </Button>
              ) : (
                <Box sx={styles.boxEditActions}>
                  <Button
                    onClick={handleCancel}
                    size="small"
                    variant="outlined"
                    disabled={salvando}
                  >
                    Cancelar
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={salvar}
                    disabled={!alterado || salvando}
                    startIcon={
                      salvando && <CircularProgress size={14} color="inherit" />
                    }
                  >
                    Salvar
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {!!snackbar && (
        <Snackbar
          open
          onClose={() => setSnackbar(null)}
          autoHideDuration={4000}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert {...snackbar} onClose={() => setSnackbar(null)} />
        </Snackbar>
      )}
    </>
  );
}
